/** Shared helpers for account / loan switcher headers (LOS, LMS, invoice). */

export const TEAL_AVATAR_BG = "#D6EFED";
export const TEAL_AVATAR_FG = "#0A6B67";
export const MENU_SEPARATOR = "#E8E8E8";

export function businessInitials(name) {
  if (!name || typeof name !== "string") return "—";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "—";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export const getApplicationId = (app) => app?.uuid ?? app?.id;

/** Route params and API fields may disagree on type (number vs string) or letter case (UUIDs). */
export function sameApplicationKey(a, b) {
  if (a == null || b == null) return false;
  const x = String(a).trim();
  const y = String(b).trim();
  if (x === "" || y === "") return false;
  return x === y || x.toLowerCase() === y.toLowerCase();
}

/**
 * Collects every id the backend might use to tie a list row to `/lms/:loanUUID` or `/invoice/:loanUuid`.
 */
export function collectLoanRouteIdentifiers(record) {
  if (!record || typeof record !== "object") return [];
  const out = [];
  const add = (v) => {
    if (v == null) return;
    const s = String(v).trim();
    if (s !== "") out.push(s);
  };

  add(record.uuid);
  add(record.id);
  add(record.loan_uuid);
  add(record.loanUuid);
  add(record.application_uuid);
  add(record.applicationUuid);
  add(record.loan_id);
  add(record.loanId);

  const loan = record.loan;
  if (loan && typeof loan === "object") {
    add(loan.uuid);
    add(loan.id);
    add(loan.loan_uuid);
    add(loan.loanUuid);
  }

  const application = record.application;
  if (application && typeof application === "object") {
    add(application.uuid);
    add(application.id);
    add(application.loan_uuid);
    add(application.loanUuid);
  }

  return out;
}

/** Prefer application uuid/id for routing; fall back to any collected loan/list id so paths match the URL segment. */
export function getRoutingSegmentId(app) {
  const primary = getApplicationId(app);
  if (primary != null && String(primary).trim() !== "") return primary;
  const ids = collectLoanRouteIdentifiers(app);
  return ids[0] ?? null;
}

/**
 * True when the URL loan segment refers to this list row (direct id match or overlap with current loan summary).
 */
export function applicationMatchesRouteParam(app, routeLoanUuid, loanSummary = null) {
  if (routeLoanUuid == null || String(routeLoanUuid).trim() === "") return false;
  const route = String(routeLoanUuid).trim();
  const appIds = collectLoanRouteIdentifiers(app);
  if (appIds.some((k) => sameApplicationKey(k, route))) return true;

  if (loanSummary && typeof loanSummary === "object") {
    const summaryIds = collectLoanRouteIdentifiers(loanSummary);
    const routeInSummary = summaryIds.some((k) => sameApplicationKey(k, route));
    if (routeInSummary) {
      return summaryIds.some((sid) => appIds.some((aid) => sameApplicationKey(sid, aid)));
    }
  }

  return false;
}

export const isInvoice = (app) =>
  String(app?.type_of_financing ?? "").toLowerCase().trim() === "invoice";

export const isRevenue = (app) =>
  String(app?.type_of_financing ?? "").toLowerCase().trim() === "revenue";

const normalizeStageToken = (v) => String(v ?? "").toLowerCase().trim();

/**
 * Active or closed: product surfaces (LMS / invoice) are available; otherwise LOS apply.
 * List APIs may omit top-level `stage` but still send `display_stage`, nested `loan.stage`, or `status`.
 */
export const isActiveLoanStage = (app) => {
  if (!app || typeof app !== "object") return false;
  const tokens = [
    app.stage,
    app.loan?.stage,
    app.status,
    app.loan?.status,
  ];
  if (tokens.some((t) => ["active", "closed"].includes(normalizeStageToken(t)))) {
    return true;
  }
  const display = normalizeStageToken(app.display_stage);
  return display === "active" || display === "closed";
};

/**
 * Same routing rules as `navigator.jsx`: invoice → /invoice/:id when serviced; revenue → /lms/:id/dashboard;
 * anything still in progress → /los/:id/apply. Unknown financing type defaults like revenue when serviced.
 */
export function applicationPath(app) {
  const id = getRoutingSegmentId(app);
  if (!id) return null;
  const serviced = isActiveLoanStage(app);
  if (isInvoice(app)) {
    return serviced ? `/invoice/${id}` : `/los/${id}/apply`;
  }
  if (isRevenue(app)) {
    return serviced ? `/lms/${id}/dashboard` : `/los/${id}/apply`;
  }
  return serviced ? `/lms/${id}/dashboard` : `/los/${id}/apply`;
}

export function fallbackPathFromSummary(loanUUID, loanSummary) {
  if (!loanUUID) return null;
  const stage = String(loanSummary?.stage ?? loanSummary?.status ?? "")
    .toLowerCase()
    .trim();
  const serviced = ["active", "closed"].includes(stage);
  return serviced ? `/lms/${loanUUID}/dashboard` : `/los/${loanUUID}/apply`;
}

function stageRowMeta(app) {
  const statusLabel = app.display_stage.trim() || '-';
  const lower = statusLabel.toLowerCase();
  const isClosed = lower === "closed";
  return { statusLabel, isClosed };
}

function formatHeadlineStatus(app) {
  const parts = [app?.status, app?.stage].filter((x) => x != null && String(x).trim() !== "");
  if (parts.length === 0) return "—";
  return parts
    .map((x) => {
      const s = String(x).trim();
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    })
    .join(" · ");
}

/** API may send `loan_identifier` (snake) or `loanIdentifier` (camel). */
function normalizeLoanIdentifier(record) {
  const raw = record?.loan_identifier ?? record?.loanIdentifier;
  if (raw == null) return null;
  const s = String(raw).trim();
  return s === "" ? null : s;
}

/**
 * When the row is an application (not serviced loan), prefer `loan_identifier` over numeric `id` / uuid slice.
 */
function applicationLabelSuffix(app, uuidOrPk) {
  const loanId = normalizeLoanIdentifier(app);
  if (loanId) return loanId;
  const numericId = app.id;
  if (numericId != null && numericId !== "") return String(numericId);
  return `${String(uuidOrPk).slice(0, 8)}…`;
}

function loanLabelSuffix(app, uuidOrPk) {
  const numericId = app.id;
  if (numericId != null && numericId !== "") return String(numericId);
  return `${String(uuidOrPk).slice(0, 8)}…`;
}

export function buildLoanRows(applications, loanUUID, loanSummary) {
  const {
    loanAccountNumber,
    disbursedAmount: loanAmount,
    status: loanStatus,
  } = loanSummary || {};

  const appsWithId = applications.filter((a) => getRoutingSegmentId(a));

  if (appsWithId.length > 0) {
    const rows = appsWithId.map((app) => {
      const id = getRoutingSegmentId(app);
      const isLmsEnabled = isActiveLoanStage(app);
      const labelPrefix = isLmsEnabled ? "Loan #" : "Application #";
      const acct =
        app.vendor_reference_id ??
        app.loan_account_no ??
        app.loan_id ??
        null;
      const label = acct
        ? `${labelPrefix}${acct}`
        : `${labelPrefix}${isLmsEnabled ? loanLabelSuffix(app, id) : applicationLabelSuffix(app, id)}`;
      const amt = 
        app.disbursed_amount ?? 
        app.loan_amount_requested ??
        app.original_loan_amount ??
        null;
      const { statusLabel, isClosed } = stageRowMeta(app);
      const headlineStatus = formatHeadlineStatus(app);
      const targetPath = applicationPath(app);
      return {
        id,
        label,
        amount: amt,
        statusLabel,
        headlineStatus,
        isClosed,
        isSelected: applicationMatchesRouteParam(app, loanUUID, loanSummary),
        targetPath,
      };
    });
    return rows.sort((a, b) => {
      if (a.isSelected && !b.isSelected) return -1;
      if (!a.isSelected && b.isSelected) return 1;
      return 0;
    });
  }

  if (loanUUID && loanSummary) {
    const st = String(loanStatus ?? "").trim();
    const lower = st.toLowerCase();
    const statusLabel = st
      ? st.charAt(0).toUpperCase() + st.slice(1).toLowerCase()
      : "Active";
    const headlineStatus = statusLabel;
    const isLmsEnabled = isActiveLoanStage(loanSummary);
    const labelPrefix = isLmsEnabled ? "Loan #" : "Application #";
    const summaryLoanIdentifier = !isLmsEnabled ? normalizeLoanIdentifier(loanSummary) : null;
    const label =
      loanAccountNumber != null && String(loanAccountNumber).trim() !== ""
        ? `${labelPrefix}${loanAccountNumber}`
        : summaryLoanIdentifier
          ? `${labelPrefix}${summaryLoanIdentifier}`
          : `${labelPrefix}`;
    return [
      {
        id: loanUUID,
        label,
        amount: loanAmount,
        statusLabel,
        headlineStatus,
        isClosed: lower === "closed",
        isSelected: true,
        targetPath: fallbackPathFromSummary(loanUUID, loanSummary),
      },
    ];
  }

  return [];
}

/** Title-case words for names/business; emails only get a leading capital (rest unchanged). */
export function capitalizeForDisplay(value) {
  if (value == null) return "";
  const s = String(value).trim();
  if (!s) return "";
  if (s.includes("@")) {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }
  return s
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * @param {object | null} loanSummary
 * @param {{ full_name?: string; email?: string } | null} userDetails
 */
export function resolveAccountHeadlines(loanSummary, userDetails) {
  const { loanAccountNumber, customerName: businessName } = loanSummary || {};
  const name = userDetails?.full_name?.trim();
  const email = userDetails?.email?.trim();

  if (loanAccountNumber != null && String(loanAccountNumber).trim() !== "") {
    const acct = String(loanAccountNumber).trim();
    const rawLine = businessName || name || email || "Business name";
    return {
      accountTitle: `Loan #${acct}`,
      businessLine: capitalizeForDisplay(rawLine),
    };
  }

  return {
    accountTitle: capitalizeForDisplay(name || email || "My account"),
    businessLine: capitalizeForDisplay(email || name || "Signed in"),
  };
}
